import { useState } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#07070e' },
  card: { width: 380, background: '#0c0c18', border: '0.5px solid #1a1a2e', borderRadius: 14, padding: '36px 32px' },
  logo: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 },
  icon: { width: 32, height: 32, background: '#c8b48a', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoText: { fontSize: 15, fontWeight: 500, letterSpacing: '0.1em', color: '#c8b48a' },
  adminBadge: { fontSize: 9, color: '#7a7888', letterSpacing: '0.1em', marginLeft: 4 },
  heading: { fontSize: 18, fontWeight: 500, color: '#d4ccc0', marginBottom: 6 },
  sub: { fontSize: 13, color: '#7a7888', marginBottom: 28, lineHeight: 1.5 },
  field: { marginBottom: 14 },
  label: { fontSize: 11, color: '#32303e', letterSpacing: '0.06em', marginBottom: 6, display: 'block' },
  btn: { width: '100%', background: '#c8b48a', color: '#07070e', border: 'none', borderRadius: 8, padding: '11px', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginTop: 8 },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  err: { fontSize: 12, color: '#c05040', marginTop: 10, background: '#1e0f0f', padding: '8px 12px', borderRadius: 6 },
  codeInput: { textAlign: 'center', fontSize: 28, fontWeight: 500, letterSpacing: '0.3em', padding: '14px', color: '#c8b48a' },
  qrBox: { background: '#111120', border: '0.5px solid #1a1a2e', borderRadius: 10, padding: 20, textAlign: 'center', marginBottom: 20 },
  steps: { marginBottom: 20 },
  step: { fontSize: 12, color: '#7a7888', padding: '5px 0', display: 'flex', gap: 10, alignItems: 'flex-start', lineHeight: 1.5 },
  stepNum: { width: 18, height: 18, borderRadius: '50%', background: '#3a3020', color: '#c8b48a', fontSize: 10, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  secretBox: { background: '#111120', border: '0.5px solid #1a1a2e', borderRadius: 6, padding: '8px 12px', fontFamily: 'monospace', fontSize: 12, color: '#7a7888', wordBreak: 'break-all', marginBottom: 14, textAlign: 'center' },
  back: { fontSize: 12, color: '#7a7888', background: 'none', border: 'none', cursor: 'pointer', marginTop: 14, display: 'block', textAlign: 'center', width: '100%' },
};

const STEPS = { USERNAME: 'username', SCAN_QR: 'scan_qr', ENTER_CODE: 'enter_code' };

export default function AdminLogin({ onLogin }) {
  const [step, setStep] = useState(STEPS.USERNAME);
  const [username, setUsername] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUsername = async () => {
    if (!username.trim()) { setError('Enter your admin username'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid credentials');
      if (data.requires_2fa_setup) {
        const setupRes = await fetch(`${BASE}/admin/setup-2fa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: username.trim() }),
        });
        const setupData = await setupRes.json();
        if (setupData.qr_url) { setQrUrl(setupData.qr_url); setSecret(setupData.secret); setStep(STEPS.SCAN_QR); }
      } else {
        setStep(STEPS.ENTER_CODE);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleCodeChange = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 6);
    setTotpCode(clean);
    setError('');
  };

  const handleVerify = async () => {
    if (totpCode.length !== 6) { setError('Enter the 6-digit code from Microsoft Authenticator'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BASE}/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), totp_code: totpCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      localStorage.setItem('shaheen_admin_token', data.token || '');
      localStorage.setItem('shaheen_admin_username', username.trim());
      onLogin(username.trim());
    } catch (e) { setError(e.message); setTotpCode(''); }
    finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center"}}><svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 2L4 5.5V11c0 4.97 3.45 9.61 8 11 4.55-1.39 8-6.03 8-11V5.5L12 2z" fill="#c8b48a" opacity="0.2" stroke="#c8b48a" strokeWidth="1.5" strokeLinejoin="round"/><path d="M8.5 12.5L11 15L15.5 9.5" stroke="#c8b48a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
          <span style={s.logoText}>SHAHEEN <span style={s.adminBadge}>ADMIN</span></span>
        </div>

        {step === STEPS.USERNAME && <>
          <div style={s.heading}>Admin access</div>
          <div style={s.sub}>Enter your admin username. You will verify with Microsoft Authenticator.</div>
          <div style={s.field}>
            <span style={s.label}>ADMIN USERNAME</span>
            <input value={username} onChange={e => { setUsername(e.target.value); setError(''); }}
              placeholder="Your admin username" onKeyDown={e => e.key === 'Enter' && handleUsername()} autoFocus />
          </div>
          {error && <div style={s.err}>{error}</div>}
          <button style={s.btn} onClick={handleUsername} disabled={loading}>{loading ? 'Checking...' : 'Continue'}</button>
        </>}

        {step === STEPS.SCAN_QR && <>
          <div style={s.heading}>Set up Microsoft Authenticator</div>
          <div style={s.sub}>Scan this QR code once to add Shaheen Admin to your authenticator app.</div>
          <div style={s.qrBox}>
            {qrUrl ? <img src={qrUrl} alt="QR" style={{ width: 180, height: 180, border: '4px solid white', borderRadius: 8 }} /> : <div style={{ width: 180, height: 180, background: '#1a1a2e', margin: '0 auto', borderRadius: 8 }} />}
          </div>
          <div style={s.steps}>
            {['Open Microsoft Authenticator', 'Tap + → Work or school account', 'Choose Scan QR code', 'Scan the code above', 'Shaheen Admin appears in your app'].map((t, i) => (
              <div key={i} style={s.step}><div style={s.stepNum}>{i + 1}</div><span>{t}</span></div>
            ))}
          </div>
          {secret && <><div style={{ fontSize: 11, color: '#7a7888', marginBottom: 6 }}>Can't scan? Enter manually:</div><div style={s.secretBox}>{secret}</div></>}
          <button style={s.btn} onClick={() => setStep(STEPS.ENTER_CODE)}>I've added it — continue</button>
        </>}

        {step === STEPS.ENTER_CODE && <>
          <div style={s.heading}>Enter your code</div>
          <div style={s.sub}>Open Microsoft Authenticator and enter the 6-digit code for <strong style={{ color: '#c8b48a' }}>Shaheen Admin</strong>.</div>
          <div style={s.field}>
            <span style={s.label}>AUTHENTICATOR CODE</span>
            <input style={s.codeInput} value={totpCode} onChange={e => handleCodeChange(e.target.value)}
              placeholder="——————" inputMode="numeric" autoFocus maxLength={6}
              onKeyDown={e => e.key === 'Enter' && totpCode.length === 6 && handleVerify()} />
          </div>
          {error && <div style={s.err}>{error}</div>}
          <button style={{ ...s.btn, ...(loading || totpCode.length !== 6 ? s.btnDisabled : {}) }}
            onClick={handleVerify} disabled={loading || totpCode.length !== 6}>
            {loading ? 'Verifying...' : 'Sign in'}
          </button>
          <button style={s.back} onClick={() => { setStep(STEPS.USERNAME); setError(''); setTotpCode(''); }}>
            Use a different username
          </button>
        </>}
      </div>
    </div>
  );
}
